import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import { jsPDF } from 'jspdf';
import { loadBengaliFont } from './pdfFontLoader';

const getUrlPathname = (url: string): string => {
  try {
    return new URL(url).pathname;
  } catch {
    return url.split('?')[0];
  }
};

const isImage = (url: string) => /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(getUrlPathname(url));
const isPdf = (url: string) => /\.pdf$/i.test(getUrlPathname(url));
const looksLikeFileName = (value: string) => /\.[a-z0-9]{2,5}$/i.test(value.trim());

const blobToDataURL = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

const loadImageAssetForPdf = async (blob: Blob): Promise<{
  dataUrl: string;
  width: number;
  height: number;
  format: 'JPEG' | 'PNG';
}> => {
  const normalizedType = blob.type.toLowerCase();
  const format: 'JPEG' | 'PNG' = normalizedType.includes('png') ? 'PNG' : 'JPEG';

  if ('createImageBitmap' in window) {
    try {
      const bitmap = await createImageBitmap(blob, { imageOrientation: 'from-image' } as any);
      const canvas = document.createElement('canvas');
      canvas.width = bitmap.width;
      canvas.height = bitmap.height;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        throw new Error('Canvas context unavailable');
      }

      ctx.drawImage(bitmap, 0, 0);
      const mimeType = format === 'PNG' ? 'image/png' : 'image/jpeg';
      const dataUrl = canvas.toDataURL(mimeType, 0.95);
      bitmap.close();

      return {
        dataUrl,
        width: canvas.width,
        height: canvas.height,
        format,
      };
    } catch (error) {
      console.warn('Falling back to Image loader for PDF asset', error);
    }
  }

  const dataUrl = await blobToDataURL(blob);
  const img = new Image();
  img.src = dataUrl;
  await new Promise((resolve, reject) => {
    img.onload = resolve;
    img.onerror = reject;
  });

  return {
    dataUrl,
    width: img.width,
    height: img.height,
    format,
  };
};

interface ApplicationData {
  application: any;
  profile: any;
}

export const useMergedPdfDownload = () => {
  const getDocumentTitle = (file: any, fallback = 'Document') => {
    const rawLabel = String(file?.label || '').trim();
    if (!rawLabel) {
      return fallback;
    }

    if (looksLikeFileName(rawLabel)) {
      return fallback;
    }

    return rawLabel;
  };

  const getIdentityNumber = (identity: any) =>
    identity?.number || '';

  const getIdentityDocs = (identity: any) => {
    const rawDocs = identity?.docFiles || [];

    if (!Array.isArray(rawDocs)) {
      return [];
    }

    return rawDocs
      .filter((doc) => doc?.url)
      .filter((doc) => ['nid', 'passport', 'birth-reg'].includes(String(doc?.type || '').toLowerCase()))
      .slice(0, 4);
  };

  const loadWatermarkLogoBytes = async () => {
    try {
      const response = await fetch('/logo.png');
      if (!response.ok) {
        return null;
      }

      return await response.arrayBuffer();
    } catch (error) {
      console.error('Failed to load watermark logo', error);
      return null;
    }
  };

  const addWatermarkToPdf = async (pdfDoc: PDFDocument) => {
    const pages = pdfDoc.getPages();
    if (pages.length === 0) {
      return;
    }

    const watermarkLogoBytes = await loadWatermarkLogoBytes();
    const watermarkLogo = watermarkLogoBytes
      ? await pdfDoc.embedPng(watermarkLogoBytes)
      : null;

    pages.forEach((page) => {
      const { width, height } = page.getSize();
      const centerX = width / 2;
      const centerY = height / 2;

      if (watermarkLogo) {
        const scaled = watermarkLogo.scale(Math.min(width / watermarkLogo.width, height / watermarkLogo.height) * 0.68);
        page.drawImage(watermarkLogo, {
          x: centerX - (scaled.width / 2),
          y: centerY - (scaled.height / 2),
          width: scaled.width,
          height: scaled.height,
          opacity: 0.16,
        });
      }
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const calculateAge = (birthDate: string) => {
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  // Helper function to check if text contains Bengali characters
  const containsBengali = (text: string): boolean => {
    return /[\u0980-\u09FF]/.test(text);
  };

  // Helper function to set appropriate font based on text content
  const setAppropriateFont = (pdf: jsPDF, text: string, isBold: boolean = false, fontLoaded: boolean = false) => {
    if (containsBengali(text) && fontLoaded) {
      pdf.setFont('SolaimanLipi');
    } else {
      pdf.setFont('helvetica', isBold ? 'bold' : 'normal');
    }
  };

  // Custom text splitting function for Bengali text
  const splitBengaliText = (pdf: jsPDF, text: string, maxWidth: number): string[] => {
    if (!containsBengali(text)) {
      return pdf.splitTextToSize(text, maxWidth);
    }

    const words = text.split(' ');
    const lines: string[] = [];
    let currentLine = '';

    for (const word of words) {
      const testLine = currentLine ? currentLine + ' ' + word : word;
      const lineWidth = pdf.getTextWidth(testLine);

      if (lineWidth <= maxWidth) {
        currentLine = testLine;
      } else {
        if (currentLine) {
          lines.push(currentLine);
          currentLine = word;
        } else {
          // If single word is too long, we have to break it
          lines.push(word);
        }
      }
    }

    if (currentLine) {
      lines.push(currentLine);
    }

    return lines.length > 0 ? lines : [text];
  };

  const createApplicationDetailsPDF = async (applicationData: ApplicationData) => {
    const { application, profile } = applicationData;
    
    // First, calculate all content to determine final height
    let bengaliFontLoaded = false;
    const fontData = await loadBengaliFont();
    if (fontData) {
      bengaliFontLoaded = true;
    }

    const pageWidth = 210; // A4 width in mm
    let yPosition = 20;
    const lineHeight = 7;
    const leftMargin = 15;
    const rightMargin = pageWidth - 15;
    const bottomMargin = 15;
    
    // Temporary PDF to calculate content height
    const tempPdf = new jsPDF();
    if (fontData) {
      const fontDataBase64 = fontData.split(',')[1];
      tempPdf.addFileToVFS('SolaimanLipi', fontDataBase64);
      tempPdf.addFont('SolaimanLipi', 'SolaimanLipi', 'normal');
    }

    // Helper function to calculate field height
    const calcFieldHeight = (value: string, indent = 0) => {
      const maxWidth = rightMargin - leftMargin - 55 - indent;
      tempPdf.setFontSize(9);
      const lines = splitBengaliText(tempPdf, value, maxWidth);
      return lineHeight * lines.length;
    };

    // Calculate total content height
    // Page title
    yPosition += 14;
    // Profile picture
    if (profile?.basic?.profilePicFile?.url) {
      yPosition += 35 + 8; // Image height + spacing
      if (profile?.basic?.fullName) {
        yPosition += 15; // Name below image
      }
    } else {
      yPosition += 15; // Header height
    }

    // Application Info (first section - no gap before)
    yPosition += 8; // Section title
    if (application.status) yPosition += calcFieldHeight(application.status.replace('-', ' ').toUpperCase());
    if (application.submittedAt) yPosition += calcFieldHeight(formatDate(application.submittedAt));
    if (application.createdAt) yPosition += calcFieldHeight(formatDate(application.createdAt));
    if (application.rejectionReason) yPosition += calcFieldHeight(application.rejectionReason);
    if (application.adminNotes) yPosition += calcFieldHeight(application.adminNotes);

    // Basic Information
    yPosition += 10; // Consistent gap before section
    yPosition += 8; // Section title
    if (profile?.basic?.fullName) yPosition += calcFieldHeight(profile.basic.fullName);
    if (profile?.basic?.email) yPosition += calcFieldHeight(profile.basic.email);
    if (profile?.basic?.phone) yPosition += calcFieldHeight(profile.basic.phone);
    if (profile?.basic?.dateOfBirth) {
      yPosition += calcFieldHeight(formatDate(profile.basic.dateOfBirth));
      yPosition += calcFieldHeight(calculateAge(profile.basic.dateOfBirth) + ' years');
    }

    // Identity Information
    yPosition += 10; // Consistent gap before section
    yPosition += 8; // Section title
    const identityNumber = getIdentityNumber(profile?.identity);
    const identityDocs = getIdentityDocs(profile?.identity);
    if (identityNumber) yPosition += calcFieldHeight(identityNumber);
    if (identityDocs.length > 0) {
      yPosition += calcFieldHeight(identityDocs.length + ' file(s) uploaded');
    }

    // Address Information
    yPosition += 10; // Consistent gap before section
    yPosition += 8; // Section title
    if (profile?.address?.present) {
      const presentAddr = [
        profile?.address.present.street,
        profile.address.present.upazila,
        profile.address.present.district
      ].filter(Boolean).join(', ');
      if (presentAddr) yPosition += calcFieldHeight(presentAddr);
    }
    if (profile?.address?.permanent) {
      const permanentAddr = [
        profile.address.permanent.street,
        profile.address.permanent.upazila,
        profile.address.permanent.district
      ].filter(Boolean).join(', ');
      if (permanentAddr) yPosition += calcFieldHeight(permanentAddr);
    }

    // Other Information
    yPosition += 10; // Consistent gap before section
    yPosition += 8; // Section title
    if (profile?.other?.fathersName) yPosition += calcFieldHeight(profile.other.fathersName);
    if (profile?.other?.mothersName) yPosition += calcFieldHeight(profile.other.mothersName);
    if (profile?.other?.religion) yPosition += calcFieldHeight(profile.other.religion);
    if (profile?.other?.gender) yPosition += calcFieldHeight(profile.other.gender);
    if (profile?.other?.maritalStatus) yPosition += calcFieldHeight(profile.other.maritalStatus);

    // Emergency Contact
    yPosition += 10; // Consistent gap before section
    yPosition += 8; // Section title
    if (profile?.emergencyContact?.name) yPosition += calcFieldHeight(profile.emergencyContact.name);
    if (profile?.emergencyContact?.phone) yPosition += calcFieldHeight(profile.emergencyContact.phone);

    // Work Information
    if (profile?.workInfo) {
      yPosition += 10; // Consistent gap before section
      yPosition += 8; // Section title
      if (profile.workInfo.employeeId) yPosition += calcFieldHeight(profile.workInfo.employeeId);
      if (profile.workInfo.projectName) yPosition += calcFieldHeight(profile.workInfo.projectName);
      if (profile.workInfo.branch) yPosition += calcFieldHeight(profile.workInfo.branch);
      if (profile.workInfo.shift) yPosition += calcFieldHeight(profile.workInfo.shift);
      if (profile.workInfo.reference) yPosition += calcFieldHeight(profile.workInfo.reference);
    }

    // Footer
    yPosition += 10 + 8; // Space + footer height

    // Calculate final content height
    const finalContentHeight = yPosition + bottomMargin;

    // Now create the actual PDF with calculated height
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: [pageWidth, finalContentHeight]
    });

    if (fontData) {
      const fontDataBase64 = fontData.split(',')[1];
      pdf.addFileToVFS('SolaimanLipi', fontDataBase64);
      pdf.addFont('SolaimanLipi', 'SolaimanLipi', 'normal');
    }

    yPosition = 20;

    // Page title centered at the top
    pdf.setFontSize(18);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(34, 34, 34);
    pdf.text('Information', pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 14;

    // Helper function to add section
    const addSection = (title: string, isFirstSection: boolean = false) => {
      // Add consistent gap before section (except for the very first section after header)
      if (!isFirstSection) {
        yPosition += 10; // Consistent gap before each section
      }
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(220, 38, 127);
      pdf.text(title, leftMargin, yPosition);
      yPosition += 8; // Space after title
    };

    // Helper function to add field
    const addField = (label: string, value: string, indent = 0) => {
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(100, 100, 100);
      pdf.text(label + ':', leftMargin + indent, yPosition);

      pdf.setTextColor(0, 0, 0);
      setAppropriateFont(pdf, value, false, bengaliFontLoaded);

      const maxWidth = rightMargin - leftMargin - 55 - indent;
      const lines = splitBengaliText(pdf, value, maxWidth);

      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        setAppropriateFont(pdf, line, false, bengaliFontLoaded);
        pdf.text(line, leftMargin + 55 + indent, yPosition + (i * lineHeight));
      }

      yPosition += lineHeight * lines.length;
    };

    // Add profile picture at the top, centered
    if (profile?.basic?.profilePicFile?.url) {
      try {
        const response = await fetch(profile.basic.profilePicFile.url);
        if (response.ok) {
          const blob = await response.blob();
          const imageAsset = await loadImageAssetForPdf(blob);

          const maxImgHeight = 35;
          let imgWidth = imageAsset.width;
          let imgHeight = imageAsset.height;
          if (imgHeight > maxImgHeight) {
            imgWidth = (imgWidth * maxImgHeight) / imgHeight;
            imgHeight = maxImgHeight;
          }
          const x = (pageWidth - imgWidth) / 2;
          const y = yPosition;
          pdf.addImage(imageAsset.dataUrl, imageAsset.format, x, y, imgWidth, imgHeight);
          yPosition += imgHeight + 8;

          if (profile?.basic?.fullName) {
            pdf.setFontSize(16);
            setAppropriateFont(pdf, profile.basic.fullName, true, bengaliFontLoaded);
            pdf.setTextColor(34, 34, 34);
            pdf.text(profile.basic.fullName, pageWidth / 2, yPosition, { align: 'center' });
            yPosition += 15;
          }
        }
      } catch (err) {
        console.error('Failed to load profile picture for PDF', err);
      }
    }

    // Header (if no profile picture)
    if (!profile?.basic?.profilePicFile?.url) {
      pdf.setFontSize(18);
      pdf.setTextColor(220, 38, 127);
      pdf.text('APPLICATION DETAILS', pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 15;
    }

    // Application Info
    addSection('APPLICATION INFORMATION', true);
    if (application.status) addField('Status', application.status.replace('-', ' ').toUpperCase());
    if (application.submittedAt) addField('Submitted At', formatDate(application.submittedAt));
    if (application.createdAt) addField('Created At', formatDate(application.createdAt));
    if (application.rejectionReason) addField('Rejection Reason', application.rejectionReason);
    if (application.adminNotes) addField('Admin Notes', application.adminNotes);

    // Basic Information
    addSection('BASIC INFORMATION');
    if (profile?.basic?.fullName) addField('Full Name', profile.basic.fullName);
    if (profile?.basic?.email) addField('Email', profile.basic.email);
    if (profile?.basic?.phone) addField('Phone', profile.basic.phone);
    if (profile?.basic?.dateOfBirth) {
      addField('Date of Birth', formatDate(profile.basic.dateOfBirth));
      addField('Age', calculateAge(profile.basic.dateOfBirth) + ' years');
    }

    // Identity Information
    addSection('IDENTITY INFORMATION');
    if (identityNumber) addField('Identity Number', identityNumber);
    if (identityDocs.length > 0) {
      addField('Identity Documents', identityDocs.length + ' file(s) uploaded');
    }

    // Address Information
    addSection('ADDRESS INFORMATION');
    if (profile?.address?.present) {
      const presentAddr = [
        profile?.address.present.street,
        profile.address.present.upazila,
        profile.address.present.district
      ].filter(Boolean).join(', ');
      if (presentAddr) addField('Present Address', presentAddr);
    }

    if (profile?.address?.permanent) {
      const permanentAddr = [
        profile.address.permanent.street,
        profile.address.permanent.upazila,
        profile.address.permanent.district
      ].filter(Boolean).join(', ');
      if (permanentAddr) addField('Permanent Address', permanentAddr);
    }

    // Other Information
    addSection('OTHER INFORMATION');
    if (profile?.other?.fathersName) addField("Father's Name", profile.other.fathersName);
    if (profile?.other?.mothersName) addField("Mother's Name", profile.other.mothersName);
    if (profile?.other?.religion) addField('Religion', profile.other.religion);
    if (profile?.other?.gender) addField('Gender', profile.other.gender);
    if (profile?.other?.maritalStatus) addField('Marital Status', profile.other.maritalStatus);

    // Emergency Contact
    addSection('EMERGENCY CONTACT');
    if (profile?.emergencyContact?.name) addField('Name', profile.emergencyContact.name);
    if (profile?.emergencyContact?.phone) addField('Phone', profile.emergencyContact.phone);

    // Work Information
    if (profile?.workInfo) {
      addSection('WORK INFORMATION');
      if (profile.workInfo.employeeId) addField('Employee ID', profile.workInfo.employeeId);
      if (profile.workInfo.projectName) addField('Project Name', profile.workInfo.projectName);
      if (profile.workInfo.branch) addField('Branch', profile.workInfo.branch);
      if (profile.workInfo.shift) addField('Shift', profile.workInfo.shift);
      if (profile.workInfo.reference) addField('Reference', profile.workInfo.reference);
    }

    // Footer
    yPosition += 10;
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(100, 100, 100);
    pdf.text('Generated on ' + new Date().toLocaleDateString(), pageWidth / 2, yPosition, { align: 'center' });

    return pdf.output('arraybuffer');
  };

  const downloadAndViewMergedPdf = async (files: any[], applicationData?: ApplicationData) => {
    const profile = applicationData?.profile;
    try {
      const mergedPdf = await PDFDocument.create();

      // First, add application details page if data is provided
      if (applicationData) {
        const detailsPdfBytes = await createApplicationDetailsPDF(applicationData);
        const detailsPdf = await PDFDocument.load(detailsPdfBytes);
        const detailsPages = await mergedPdf.copyPages(detailsPdf, detailsPdf.getPageIndices());
        detailsPages.forEach(page => mergedPdf.addPage(page));
      }

      // Filter out profile picture from files to avoid duplication
      const filteredFiles = files.filter(f => !(profile?.basic?.profilePicFile?.url && f.url === profile.basic.profilePicFile.url));

      // Categorize files — URL extension first, then content-type for unrecognized URLs
      const imageDocs: any[] = [];
      const pdfDocs: any[] = [];

      await Promise.all(filteredFiles.map(async (f) => {
        if (isImage(f.url)) {
          imageDocs.push(f);
        } else if (isPdf(f.url)) {
          pdfDocs.push(f);
        } else {
          try {
            const safeUrl = f.url.replace(/^http:\/\//i, 'https://');
            const headRes = await fetch(safeUrl, { method: 'HEAD' });
            const contentType = headRes.headers.get('content-type') || '';
            if (contentType.startsWith('image/')) {
              imageDocs.push(f);
            } else if (contentType.includes('application/pdf')) {
              pdfDocs.push(f);
            } else {
              console.warn('Unrecognized file type, skipping:', f.url, contentType);
            }
          } catch {
            console.warn('Could not determine file type for:', f.url);
          }
        }
      }));

      // Group specific document types
      const nidImages = imageDocs.filter(f => f.label && f.label.toLowerCase().includes('nid'));
      const passportImages = imageDocs.filter(f => f.label && f.label.toLowerCase().includes('passport'));
      const otherImages = imageDocs.filter(f => !((f.label && (f.label.toLowerCase().includes('nid') || f.label.toLowerCase().includes('passport')))));

      // Helper to determine side
      const getSide = (label: string) => {
        if (label.toLowerCase().includes('front') || label.toLowerCase().includes('first')) return 'Front';
        if (label.toLowerCase().includes('back') || label.toLowerCase().includes('second')) return 'Back';
        return 'Document';
      };

      // Function to add image pairs on same page with vertical stacking
      const addImagePairVertical = async (pair: any[], titlePrefix: string = '') => {
        const tempDoc = new jsPDF();
        const pageWidth = tempDoc.internal.pageSize.getWidth();
        const pageHeight = tempDoc.internal.pageSize.getHeight();
        const margin = 10;
        const maxImgWidth = pageWidth - margin * 2;
        const labelFontSize = 10;
        const maxImgHeight = (pageHeight - margin * 3 - pair.length * 20) / pair.length;

        let y = margin;
        let imageAdded = false;

        for (let j = 0; j < pair.length; j++) {
          const file = pair[j];
          if (!file) continue;

          try {
            const fileUrl = file.url.replace(/^http:\/\//i, 'https://');
            const response = await fetch(fileUrl);
            if (!response.ok) {
              console.warn(`Failed to fetch file (status ${response.status}):`, fileUrl);
              continue;
            }

            const blob = await response.blob();
            const imageAsset = await loadImageAssetForPdf(blob);

            // Calculate proper dimensions maintaining aspect ratio
            let imgWidth = imageAsset.width;
            let imgHeight = imageAsset.height;
            const aspectRatio = imgWidth / imgHeight;

            // Fit to width first
            if (imgWidth > maxImgWidth) {
              imgWidth = maxImgWidth;
              imgHeight = imgWidth / aspectRatio;
            }

            // Then check height and adjust if needed
            if (imgHeight > maxImgHeight) {
              imgHeight = maxImgHeight;
              imgWidth = imgHeight * aspectRatio;
            }

            // Center horizontally
            const x = (pageWidth - imgWidth) / 2;

            tempDoc.setFontSize(labelFontSize);
            tempDoc.setFont('helvetica', 'bold');
            const label = titlePrefix
              ? `${titlePrefix} (${getSide(file.label)})`
              : getDocumentTitle(file);
            tempDoc.text(label, pageWidth / 2, y + 5, { align: 'center' });
            tempDoc.setFont('helvetica', 'normal');

            tempDoc.addImage(imageAsset.dataUrl, imageAsset.format, x, y + 10, imgWidth, imgHeight);
            y += imgHeight + 30;
            imageAdded = true;
          } catch (err) {
            console.error(`Failed to load or process file: ${file.url}`, err);
          }
        }

        // Only add page if at least one image was successfully loaded
        if (!imageAdded) return;

        const tempPdfBytes = tempDoc.output('arraybuffer');
        const tempPdf = await PDFDocument.load(tempPdfBytes);
        const copiedPages = await mergedPdf.copyPages(tempPdf, tempPdf.getPageIndices());
        copiedPages.forEach(page => mergedPdf.addPage(page));
      };

      // Handle NID images (front/back on same page, vertically)
      if (nidImages.length > 0) {
        const front = nidImages.find(f => getSide(f.label) === 'Front') || nidImages[0];
        const back = nidImages.find(f => getSide(f.label) === 'Back');
        await addImagePairVertical([front, back].filter(Boolean), 'NID');
      }

      // Handle Passport images (front/back on same page, vertically)
      if (passportImages.length > 0) {
        const front = passportImages.find(f => getSide(f.label) === 'Front') || passportImages[0];
        const back = passportImages.find(f => getSide(f.label) === 'Back');
        await addImagePairVertical([front, back].filter(Boolean), 'Passport');
      }

      // Handle other images, one per page, vertically
      for (let i = 0; i < otherImages.length; i++) {
        const single = [otherImages[i]];
        await addImagePairVertical(single);
      }

      // Handle PDF documents with human-readable titles only
      for (const file of pdfDocs) {
        try {
          const fileUrl = file.url.replace(/^http:\/\//i, 'https://');
          const response = await fetch(fileUrl);
          if (!response.ok) {
            console.warn(`Failed to fetch PDF (status ${response.status}):`, fileUrl);
            continue;
          }

          const arrayBuffer = await response.arrayBuffer();
          const label = getDocumentTitle(file);
          const pdf = await PDFDocument.load(arrayBuffer);
          const pages = pdf.getPages();

          if (pages.length > 0) {
            const firstPage = pages[0];
            const { width, height } = firstPage.getSize();
            const helveticaBoldFont = await pdf.embedFont(StandardFonts.HelveticaBold);
            const fontSize = 10;
            const titleTopMargin = 28;
            const titleBottomMargin = 3;
            const titleHeight = titleTopMargin + fontSize + titleBottomMargin;
            const newHeight = height + titleHeight;

            firstPage.setSize(width, newHeight);
            firstPage.drawRectangle({
              x: 0,
              y: newHeight - titleHeight,
              width: width,
              height: titleHeight,
              color: rgb(1, 1, 1),
            });

            const textWidth = helveticaBoldFont.widthOfTextAtSize(label, fontSize);
            const titleY = newHeight - titleTopMargin - (fontSize * 0.3);

            firstPage.drawText(label, {
              x: (width - textWidth) / 2,
              y: titleY,
              size: fontSize,
              font: helveticaBoldFont,
              color: rgb(0, 0, 0),
            });
          }

          const copiedPages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
          copiedPages.forEach(page => mergedPdf.addPage(page));
        } catch (err) {
          console.error(`Failed to load or process file: ${file.url}`, err);
        }
      }

      await addWatermarkToPdf(mergedPdf);

      const finalPdf = await mergedPdf.save();
      const blob = new Blob([new Uint8Array(finalPdf)], { type: 'application/pdf' });

      const filename = applicationData?.application?._id
        ? `${applicationData?.profile?.basic?.fullName}-${applicationData.application._id.slice(-4)}.pdf`
        : 'application.pdf';

      // Download
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      link.click();

      // Open in new tab
      const viewUrl = URL.createObjectURL(blob);
      window.open(viewUrl, '_blank');

      // Clean up
      setTimeout(() => {
        URL.revokeObjectURL(link.href);
        URL.revokeObjectURL(viewUrl);
      }, 100);

    } catch (error) {
      console.error('Error creating merged PDF:', error);
      throw error;
    }
  };

  return { downloadAndViewMergedPdf };
};
